/**
 * LLM Provider Configuration page.
 * Allows tenants to configure API keys and connection settings for LLM providers.
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Key as KeyIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Cloud as CloudIcon,
} from '@mui/icons-material';
import { llmService } from '../services/llmService';
import { useNotificationStore } from '../store/notificationStore';
import { ProviderConfigForm } from '../components/llm/ProviderConfigForm';
import type { LlmProviderConfigDto, LlmProviderDto } from '../types/llm';

export const LlmProviderConfigPage = () => {
  const [configs, setConfigs] = useState<LlmProviderConfigDto[]>([]);
  const [providers, setProviders] = useState<LlmProviderDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<LlmProviderConfigDto | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingConfig, setDeletingConfig] = useState<LlmProviderConfigDto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { addNotification } = useNotificationStore();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [configsData, providersData] = await Promise.all([
        llmService.getProviderConfigs(),
        llmService.getProviders(true),
      ]);
      setConfigs(configsData);
      setProviders(providersData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load provider configurations');
      addNotification(err.response?.data?.message || 'Failed to load provider configurations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingConfig(null);
    setFormOpen(true);
  };

  const handleEdit = (config: LlmProviderConfigDto) => {
    setEditingConfig(config);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingConfig(null);
  };

  const handleFormSaved = () => {
    setFormOpen(false);
    setEditingConfig(null);
    loadData();
  };

  const handleDeleteClick = (config: LlmProviderConfigDto) => {
    setDeletingConfig(config);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingConfig) return;

    setDeleting(true);
    try {
      await llmService.deleteProviderConfig(deletingConfig.id);
      addNotification(`${deletingConfig.providerName} configuration deleted`, 'success');
      setDeleteDialogOpen(false);
      setDeletingConfig(null);
      loadData();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete configuration', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Provider IDs that already have a config for this tenant.
  // We still pass the FULL providers list to the form so users can see every
  // option (e.g. OpenAI) and understand why some are disabled, instead of
  // having them silently disappear from the dropdown.
  const configuredProviderIds = new Set(configs.map((c) => c.providerId));
  const availableProviders = providers.filter((p) => !configuredProviderIds.has(p.id));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            LLM Provider Configurations
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Configure API keys and connection settings for AI model providers. Models from configured providers will be available when creating agents.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={availableProviders.length === 0}
        >
          Add Provider
        </Button>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Info about available providers */}
      {availableProviders.length === 0 && configs.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          All available providers have been configured. You can edit existing configurations below.
        </Alert>
      )}
      {providers.length === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No active LLM providers were returned by the catalog. If a provider you expect (e.g. OpenAI)
          is missing here, an administrator may need to mark it active in the provider catalog.
        </Alert>
      )}

      {/* Configs Table */}
      {configs.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <CloudIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Provider Configurations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: 'auto' }}>
              Add your first LLM provider configuration to start using AI models in your agents.
              You will need an API key from the provider.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
              Add Provider Configuration
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Base URL</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Deployment</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>API Version</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>API Key</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((config) => (
                <TableRow key={config.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CloudIcon fontSize="small" color="primary" />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {config.providerName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {config.name || '\u2014'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {config.baseUrl || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {config.deploymentName || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {config.apiVersion || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {config.hasApiKey ? (
                      <Tooltip title="API key is configured">
                        <Chip
                          icon={<KeyIcon />}
                          label={config.apiKeyLast5 ? `\u2026${config.apiKeyLast5}` : 'Configured'}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={config.apiKeyLast5 ? { fontFamily: 'monospace' } : undefined}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title="No API key configured">
                        <Chip
                          label="Not Set"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {config.isActive ? (
                      <Chip icon={<ActiveIcon />} label="Active" size="small" color="success" />
                    ) : (
                      <Chip icon={<InactiveIcon />} label="Inactive" size="small" color="default" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit configuration">
                      <IconButton size="small" onClick={() => handleEdit(config)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete configuration">
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(config)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Provider Config Form Dialog */}
      <ProviderConfigForm
        open={formOpen}
        onClose={handleFormClose}
        onSaved={handleFormSaved}
        config={editingConfig}
        providers={providers}
        configuredProviderIds={configuredProviderIds}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Provider Configuration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the configuration for{' '}
            <strong>{deletingConfig?.providerName}</strong>? This will remove the API key and
            connection settings. Agents using models from this provider will no longer work.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
